import type Owner from '@ember/owner';
import Service, { service } from '@ember/service';

import {
  VirtualNetwork,
  authorizationMiddleware,
  baseRealm,
  cardIdToURL,
  registerCardReferencePrefix,
  fetcher,
} from '@cardstack/runtime-common';

import config from '@cardstack/host/config/environment';

import { shimExternals } from '../lib/externals';
import { authErrorEventMiddleware } from '../utils/auth-error-guard';

import type LoaderService from './loader-service';
import type RealmService from './realm';
import type ResetService from './reset';

export default class NetworkService extends Service {
  @service declare loaderService: LoaderService;
  @service declare realm: RealmService;
  @service declare reset: ResetService;

  virtualNetwork = this.makeVirtualNetwork();

  constructor(owner: Owner) {
    super(owner);
    this.reset.register(this);
  }

  get fetch() {
    return this.virtualNetwork.fetch;
  }

  get resolveImport() {
    return this.virtualNetwork.resolveImport;
  }

  get authedFetch() {
    let authenticatedFetch = fetcher(this.fetch, [
      authorizationMiddleware(this.realm),
      authErrorEventMiddleware(),
    ]);
    return (url: string | URL | Request, init?: RequestInit) => {
      return authenticatedFetch(normalizeAuthedFetchURL(url), init);
    };
  }

  get mount() {
    return this.virtualNetwork.mount.bind(this.virtualNetwork);
  }

  private makeVirtualNetwork() {
    let virtualNetwork = new VirtualNetwork(globalThis.fetch);
    let resolvedBaseRealmURL = new URL(
      withTrailingSlash(config.resolvedBaseRealmURL),
    );
    virtualNetwork.addURLMapping(new URL(baseRealm.url), resolvedBaseRealmURL);
    shimExternals(virtualNetwork);
    virtualNetwork.addImportMap('@cardstack/boxel-icons/', (rest) => {
      return `${config.iconsURL}/@cardstack/boxel-icons/v1/icons/${rest}.js`;
    });
    if (config.resolvedCatalogRealmURL) {
      let catalogURL = withTrailingSlash(config.resolvedCatalogRealmURL);
      registerCardReferencePrefix('@cardstack/catalog/', catalogURL);
      virtualNetwork.addImportMap(
        '@cardstack/catalog/',
        (rest) => new URL(rest, catalogURL).href,
      );
    }
    if (config.resolvedSkillsRealmURL) {
      let skillsURL = withTrailingSlash(config.resolvedSkillsRealmURL);
      registerCardReferencePrefix('@cardstack/skills/', skillsURL);
      virtualNetwork.addImportMap(
        '@cardstack/skills/',
        (rest) => new URL(rest, skillsURL).href,
      );
    }
    if (config.resolvedOpenRouterRealmURL) {
      let openRouterURL = withTrailingSlash(config.resolvedOpenRouterRealmURL);
      registerCardReferencePrefix('@cardstack/openrouter/', openRouterURL);
      virtualNetwork.addImportMap(
        '@cardstack/openrouter/',
        (rest) => new URL(rest, openRouterURL).href,
      );
    }
    return virtualNetwork;
  }

  resetState = () => {
    this.virtualNetwork = this.makeVirtualNetwork();
  };
}

declare module '@ember/service' {
  interface Registry {
    network: NetworkService;
  }
}

function withTrailingSlash(url: string): string {
  return url.endsWith('/') ? url : `${url}/`;
}

function normalizeAuthedFetchURL(url: string | URL | Request) {
  if (url instanceof URL || url instanceof Request) {
    return url;
  }
  try {
    return cardIdToURL(url).href;
  } catch {
    return url;
  }
}
